/**
 * What every window receives: the services built once by `services.ts` (from the list in `subsystems.ts`) and passed down with
 * Solid's context, so windows never create these themselves or import each other.
 *
 * Each field is an independent service with its own file; none of them knows about windows.
 */
import { createContext, useContext } from "solid-js";
import type { Actions } from "../client/actions";
import type { Client } from "../client/client";
import type { NodeAdder } from "../state/nodeAdder";
import type { Pages } from "../state/pages";
import type { Play } from "../state/play";
import type { PrototypeService } from "../state/prototype";
import type { Documents } from "../state/documents";
import type { GraphMirror } from "../client/graph";
import type { Commands } from "../shared/commands";
import type { ConfigMirror } from "../state/config";
import type { KindsService } from "../state/kinds";
import type { Fields } from "../state/fields";
import type { ContextMenu } from "../state/menu";
import type { Shell } from "../state/shell";
import type { Palette } from "../state/palette";
import type { InspectorSections } from "../state/inspector";
import type { PresenceService } from "../state/presence";
import type { AccessService } from "../state/access";
import type { Presets } from "../state/presets";
import type { AssetsService } from "../state/assets";
import type { Project } from "../state/project";
import type { Ai } from "../state/ai";
import type { Analysis } from "../state/analysis";
import type { Plugins } from "../state/plugins";
import type { PluginViews } from "../state/pluginViews";
import type { Diagram } from "../state/diagram";
import type { Board } from "../state/board";
import type { Scene } from "../state/scene";
import type { SceneSync } from "../state/sceneSync";
import type { Spaces } from "../state/spaces";
import type { Accounts } from "../state/accounts";
import type { View } from "../state/view";
import type { WindowManager } from "../wm/manager";
import type { Environments } from "../state/environments";
import type { Theme } from "../state/theme";

/** The services shared by the whole GUI. */
export interface App {
  /** The connection to the core (`client/client.ts`). */
  client: Client;
  /** The core's graph, mirrored (`client/graph.ts`). */
  graph: GraphMirror;
  /** Every edit, as a function (`client/actions.ts`). */
  actions: Actions;
  /** Selection, search, filters, link tool (`state/view.ts`). */
  view: View;
  /** The graph as laid out on screen: positions, zoom (`state/scene/`). */
  scene: Scene;
  /** Sends the scene's positions to the core (`state/sceneSync.ts`). */
  sceneSync: SceneSync;
  /** The class diagram: its layout, view and boxes moved by hand (`state/diagram/`). */
  diagram: Diagram;
  /** The board: items placed freely, frames, its view and gestures (`state/board/`). */
  board: Board;
  /** UI/UX's prototype: the board's screens, the document played, the exported site (`state/prototype/`). */
  prototype: PrototypeService;
  /** The fields the environment worked in adds to nodes, and their choices (`state/fields.ts`). */
  fields: Fields;
  /** Node and link kinds: built-in, the graph's and the plugins' (`state/kinds.ts`). */
  kinds: KindsService;
  /** The core's configuration, mirrored (`state/config.ts`). */
  config: ConfigMirror;
  /** Node templates (`state/presets.ts`). */
  presets: Presets;
  /** Files kept with the work (`state/assets.ts`). */
  assets: AssetsService;
  /** The open project: name, file, saved or not (`state/project.ts`). */
  project: Project;
  /** Reading a project's code into the graph (`state/analysis.ts`). */
  analysis: Analysis;
  /** The plugins: built in and installed, their commands and notices (`state/plugins.ts`). */
  plugins: Plugins;
  /** The windows plugins add and what they show (`state/pluginViews.ts`). */
  pluginViews: PluginViews;
  /** AI suggestions: notes, groups, review (`state/ai.ts`). */
  ai: Ai;
  /** Who else is here: names, colours, cursors, selections (`state/presence.ts`). */
  presence: PresenceService;
  /** Who may do what here: this page's role, members and invites (`state/access.ts`). */
  access: AccessService;
  /** Which board this page works on: a space on a server, or back (`state/spaces.ts`). */
  spaces: Spaces;
  /** Signed in to servers: teams, their spaces, members and invites (`state/accounts.ts`). */
  accounts: Accounts;
  /** Light, dark or system (`app/theme.ts`). */
  theme: Theme;
  /** Named commands for shortcuts (`shared/commands.ts`). */
  commands: Commands;
  /** Windows and their layout (`wm/manager.ts`). */
  wm: WindowManager;
  /** The environments of the plugins on, and the one this page works in (`state/environments.ts`). */
  environments: Environments;
  /** The Add node window's service: where the node goes, adding it (`state/nodeAdder.ts`). */
  nodeAdder: NodeAdder;
  /** Writing's documents: the pages' files, templates, saving with merges (`state/documents/`). */
  documents: Documents;
  /** Node pages: the one shown, mentions and backlinks, following a wiki link (`state/pages.ts`). */
  pages: Pages;
  /** Playing the environment's graph as a story or a prototype, and exporting it (`state/play.ts`). */
  play: Play;
  /** The right-click menu (`state/menu.ts`). */
  menu: ContextMenu;
  /** The menu bar's menus and the toolbar (`state/shell.ts`). */
  shell: Shell;
  /** The command palette: every action, window and node, searched (`state/palette.ts`). */
  palette: Palette;
  /** Which inspector sections are folded, per environment (`state/inspector.ts`). */
  inspector: InspectorSections;
}

/** The Solid context carrying the {@link App}. */
export const AppContext = createContext<App>();

/** The {@link App}, from inside any component rendered under `<AppContext.Provider>`. */
export function useApp(): App {
  const app = useContext(AppContext);
  if (!app) throw new Error("useApp() must be used inside <AppContext.Provider>");
  return app;
}
